import { MapPin, Phone, Mail, MessageCircle, Clock, Landmark } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import EnquiryForm from "@/components/EnquiryForm";
import { Reveal } from "@/components/Reveal";
import { IMG, CONTACT } from "@/data/content";

const Contact = () => (
  <main data-testid="contact-page">
    <PageHero
      eyebrow="Contact Us"
      title="Contact Amazing Grace —"
      italic="Hotel Linen & Hospitality Textile Supplier"
      description="Reach our team for product specifications, bulk requirements, quotations and procurement enquiries — based in Karamadai, Coimbatore and serving customers across India."
      image={IMG.fabric}
    />

    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-16">
        <div className="lg:col-span-2">
          <SectionHeader number="01" eyebrow="Reach Us" title="Amazing Grace" />
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-8">
              <div className="flex gap-4">
                <MapPin size={20} className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="text-[11px] tracking-[0.24em] uppercase text-navy/50 mb-2">Address</p>
                  <p data-testid="contact-address" className="text-navy/80 text-[15px] leading-relaxed max-w-xs">
                    {CONTACT.addressLines.map((line) => (
                      <span key={line} className="block">{line}</span>
                    ))}
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone size={20} className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="text-[11px] tracking-[0.24em] uppercase text-navy/50 mb-2">Phone</p>
                  <div className="space-y-1.5">
                    {CONTACT.phones.map((p) => (
                      <a
                        key={p}
                        href={`tel:${p.replace(/\s/g, "")}`}
                        data-testid={`contact-phone-${p.replace(/[^0-9]/g, "")}`}
                        className="block text-navy/80 text-[15px] hover:text-gold transition-colors duration-300"
                      >
                        {p}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail size={20} className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="text-[11px] tracking-[0.24em] uppercase text-navy/50 mb-2">Email</p>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="contact-email-link"
                    className="text-navy/80 text-[15px] hover:text-gold transition-colors duration-300 break-all"
                  >
                    {CONTACT.email}
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Landmark size={20} className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="text-[11px] tracking-[0.24em] uppercase text-navy/50 mb-2">Business Details</p>
                  <p data-testid="contact-gstin" className="text-navy/80 text-[15px]">GSTIN: {CONTACT.gstin}</p>
                  <p data-testid="contact-pan" className="text-navy/80 text-[15px]">PAN: {CONTACT.pan}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock size={20} className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="text-[11px] tracking-[0.24em] uppercase text-navy/50 mb-2">Response</p>
                  <p className="text-navy/80 text-[15px]">Our team responds to enquiries promptly on business days.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-12">
              <a
                href="tel:+917603810376"
                data-testid="call-now-button"
                className="inline-flex items-center justify-center gap-2 bg-navy text-ivory text-[12px] tracking-[0.12em] uppercase px-6 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
              >
                <Phone size={14} /> Call Now
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="email-us-button"
                className="inline-flex items-center justify-center gap-2 border border-navy text-navy text-[12px] tracking-[0.12em] uppercase px-6 py-4 transition-colors duration-300 hover:bg-navy hover:text-ivory"
              >
                <Mail size={14} /> Email Us
              </a>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-enquiry-button"
                className="inline-flex items-center justify-center gap-2 border border-gold text-gold text-[12px] tracking-[0.12em] uppercase px-6 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
              >
                <MessageCircle size={14} /> WhatsApp Enquiry
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-3">
          <div className="bg-ivory border border-navy/10 p-8 md:p-12">
            <p className="text-[11px] tracking-[0.28em] uppercase text-gold">Product Enquiry</p>
            <h3 className="font-serif text-3xl md:text-4xl font-medium text-navy mt-3">
              Send Us an Enquiry
            </h3>
            <p className="text-navy/60 text-sm font-light mt-3 mb-10">
              Suitable for hotel procurement teams, distributors and institutional buyers.
            </p>
            <EnquiryForm />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="pb-24 md:pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <div data-testid="map-embed" className="border border-navy/10 overflow-hidden">
            <iframe
              title="Amazing Grace — Karamadai, Coimbatore"
              src={CONTACT.mapsEmbed}
              className="w-full h-[420px] grayscale-[35%] contrast-[1.02]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Contact;
